import { centsToDollars, parsePrice, isNoMaximumOOP } from "helpers/price";
import { Accordion, AccordionTab } from "primereact/accordion";
import { useState, useMemo } from "react";
import styled from "styled-components";

const StyledAccordion = styled(Accordion)`
  .p-accordion-header .p-accordion-header-link {
    background-color: transparent !important;
    border: none !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
  .p-accordion-toggle-icon {
    display: none;
  }
  .p-accordion-header-text {
    width: 100%;
    i {
      color: #524adb;
    }
  }
  .p-accordion-header:not(.p-disabled) .p-accordion-header-link:focus {
    box-shadow: none;
  }
  .p-accordion-content {
    border: none !important;
    padding-left: 5px !important;
    padding-right: 5px !important;
    padding-top: 0 !important;
    padding-bottom: 20px !important;
    color: #706f7b !important;
    font-size: 14px;
    font-weight: 400;
  }
  .p-accordion-tab {
    border-bottom: 1px solid #e0e0e0;
  }

  .p-accordion-header-text .icon {
    color: #524adb;
    font-size: 24px;
    font-weight: 400;
    background-color: #edf1fd;
    border: 1px solid #d1e2fa;
  }
`;

const StyledQuestionItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  background-color: var(--primary-50);
  padding: 10px;
  border-left: 4px solid var(--primary-400);
  margin-bottom: 10px;
  border-radius: 0px 8px 8px 0px;
`;

function Header({ title, isActive }: { title: string; isActive: boolean }) {
  return (
    <div className="flex align-items-center justify-content-between">
      <span className="vertical-align-middle black">{title}</span>
      <div className="border-circle w-2rem h-2rem bg-gray-100 flex align-items-center justify-content-center icon">
        {isActive ? "-" : "+"}
      </div>
    </div>
  );
}

function QuestionItem({ title, content }: { title?: string; content: string }) {
  return (
    <StyledQuestionItem>
      {title && <p className="font-semibold black">{title}</p>}
      <p className="text-sm">{content}</p>
    </StyledQuestionItem>
  );
}

function Questions({
  deductible,
  outOfPocket,
  coinsurance,
}: {
  deductible?: number;
  outOfPocket?: number;
  coinsurance?: number;
}) {
  const [activeIndex, setActiveIndex] = useState<number[] | []>([]);
  const showInfoLine = useMemo(() => {
    return (
      deductible !== undefined ||
      outOfPocket !== undefined ||
      coinsurance !== undefined
    );
  }, [deductible, outOfPocket, coinsurance]);

  return (
    <div className="flex flex-column gap-3 mt-4">
      <StyledAccordion
        multiple
        activeIndex={activeIndex}
        onTabChange={(e) =>
          setActiveIndex(Array.isArray(e.index) ? e.index : [])
        }
      >
        <AccordionTab
          header={
            <Header
              title="What to Do If You Receive a Bill"
              isActive={activeIndex.some((index) => index === 0)}
            />
          }
        >
          <QuestionItem content="After insurance processes your claims, bills may still be sent automatically. Don’t pay separately — your payment plan will be updated once we get the final claim details." />
          <QuestionItem content="If your balance looks different, let us know so we can adjust it. Delays can happen between when you get your bill or EOB and when we receive the updated amounts." />
        </AccordionTab>
        <AccordionTab
          header={
            <Header
              title="Why Your Balance Changed"
              isActive={activeIndex.some((index) => index === 1)}
            />
          }
        >
          <QuestionItem
            title="Insurance Benefits Corrections:"
            content={`Initial data sent to us by your insurance may be wrong. ${
              showInfoLine
                ? `Amounts received during estimate creations were: ${parsePrice(
                    centsToDollars(deductible || 0)
                  )} deductible remaining, ${
                    isNoMaximumOOP(outOfPocket)
                      ? "no maximum"
                      : parsePrice(centsToDollars(outOfPocket || 0))
                  } out-of-pocket maximum, and ${coinsurance}% coinsurance.`
                : ""
            }`}
          />
          <QuestionItem
            title="Extra procedures:"
            content="Additional services might occur during your visit that weren't in the original estimate."
          />
        </AccordionTab>
      </StyledAccordion>
    </div>
  );
}

export default Questions;
